import { useEffect, useRef } from 'react';

import { useAuth } from 'react-oidc-context';

import { appConfig } from '@/shared/config';

const COOKIE_NAME = appConfig.session.cookieName;
const COOKIE_DOMAIN = appConfig.session.cookieDomain;
const ALLOWED_DOMAINS = appConfig.session.allowedDomains;
const POLL_INTERVAL_MS = appConfig.session.pollIntervalMs;
// Tokens issued within this window are fresh (a sign-in or silent renew that just finished). Older tokens come back
// from storage and must not revive a session cookie another app cleared on purpose. The age uses the client clock,
// so the window also absorbs workstation clock skew; too short a window traps the app in a reload loop.
const FRESH_TOKEN_WINDOW_SECONDS = 300;

function isSessionSyncSupported(): boolean {
  if (!COOKIE_NAME || !COOKIE_DOMAIN || ALLOWED_DOMAINS.length === 0) return false;
  const { hostname } = window.location;
  return ALLOWED_DOMAINS.some((suffix: string) => hostname.endsWith(suffix));
}

function setCookie(value: string): void {
  document.cookie = `${COOKIE_NAME}=${encodeURIComponent(value)}; Domain=${COOKIE_DOMAIN}; Path=/; Secure; SameSite=Lax`;
}

function clearCookie(): void {
  document.cookie = `${COOKIE_NAME}=; Domain=${COOKIE_DOMAIN}; Path=/; Secure; SameSite=Lax; Expires=Thu, 01 Jan 1970 00:00:00 GMT`;
}

function readCookie(): string | null {
  const match = new RegExp(`(?:^|;\\s*)${COOKIE_NAME}=([^;]*)`).exec(document.cookie);
  return match?.[1] !== undefined ? decodeURIComponent(match[1]) : null;
}

/**
 * Cross-tab logout sync across configured *.cucollaborate.com subdomains.
 *
 * Sets a shared cookie at the configured domain keyed to the signed-in user's
 * sub, only when no cookie exists yet and the token is fresh (as in cuc-data-explorer-fe). Polls every
 * POLL_INTERVAL_MS: if the cookie disappears or no longer
 * matches, the local session is torn down so the user gets re-prompted.
 *
 * Cleared on local sign-out via the UserManager's userSignedOut / userUnloaded
 * events, so any sibling tab at another allowed subdomain detects the logout
 * within ~2 seconds. Becomes a no-op on hosts that don't match any entry of
 * VITE_SESSION_ALLOWED_DOMAINS (e.g. localhost dev), since browsers silently
 * reject cross-domain cookie writes.
 */
export function useSessionSync(): void {
  const auth = useAuth();
  const sub = auth.user?.profile.sub;
  const iat = auth.user?.profile.iat;
  const expiresAt = auth.user?.expires_at;
  const isAuthed = auth.isAuthenticated;

  const removeUserRef = useRef(auth.removeUser);
  // eslint-disable-next-line react-hooks/refs
  removeUserRef.current = auth.removeUser;

  useEffect(() => {
    if (!isSessionSyncSupported()) return;
    if (!isAuthed || !sub) return;
    if (expiresAt && expiresAt * 1000 <= Date.now()) return;
    if (readCookie() !== null) return;
    const tokenAge = iat ? Date.now() / 1000 - iat : Infinity;
    if (tokenAge > FRESH_TOKEN_WINDOW_SECONDS) return;
    setCookie(sub);
  }, [isAuthed, sub, iat, expiresAt]);

  useEffect(() => {
    if (!isSessionSyncSupported()) return;
    const offSignedOut = auth.events.addUserSignedOut(clearCookie);
    const offUnloaded = auth.events.addUserUnloaded(clearCookie);
    return () => {
      offSignedOut();
      offUnloaded();
    };
  }, [auth.events]);

  useEffect(() => {
    if (!isSessionSyncSupported()) return;
    if (!isAuthed || !sub) return;

    const id = window.setInterval(() => {
      if (readCookie() !== sub) {
        void removeUserRef.current().finally(() => {
          window.location.reload();
        });
      }
    }, POLL_INTERVAL_MS);

    return () => {
      window.clearInterval(id);
    };
  }, [isAuthed, sub]);
}
